import React, { ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppDatePicker,
  AppTextField,
  AppRadiogroup,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import {
  IApproveReject,
  handleApproveRejectDefaultValues,
} from './formDefaultValues';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import RegistrationDetailActions from '../../../../redux/reducers/registration_detail_reducer/actions';
import * as DateUtils from 'utils/dates';
import * as RegistrationDetailSelectors from '../../../../redux/reducers/registration_detail_reducer/selectors';
import { SchemeTypes } from 'config';

interface IProps {
  registration_id: string;
  commencement_date?: Date;
  registration_date?: Date;
  isExcluded: boolean;
  applicant_id: string;
  scheme: SchemeTypes;
  exclusionReason?: string;
}

export default function ResponseForm(props: IProps): ReactElement {
  // console.log('Render ApproveRejectForm');
  const classes = useStyles();
  const defaultValues = handleApproveRejectDefaultValues(props);
  const dispatch = useDispatch();

  const { errors, control, handleSubmit, watch, reset, setValue, setError } =
    useForm<IApproveReject>({
      defaultValues,
    });
  const activeRegistrations =
    RegistrationDetailSelectors.useGetApplicantActiveRegistrations();

  // useGetApplicantActiveRegistrations
  React.useEffect(() => {
    dispatch(
      RegistrationDetailActions.fetchApplicantActiveRegistrations({
        applicant_id: props.applicant_id,
      })
    );
  }, [dispatch]);

  React.useEffect(() => {
    // console.log(activeRegistrations);
    if (
      !props.isExcluded &&
      activeRegistrations &&
      activeRegistrations.active_registrations &&
      activeRegistrations.active_registrations.length > 0
    ) {
      reset({
        ...handleApproveRejectDefaultValues(props),
        ...(activeRegistrations.active_registrations[0].COMMENCEMENT_DATE && {
          commencement_date: new Date(
            activeRegistrations.active_registrations[0].COMMENCEMENT_DATE
          ),
        }),
        ...(activeRegistrations.active_registrations[0].REGISTRATION_DATE && {
          registration_date: new Date(
            activeRegistrations.active_registrations[0].REGISTRATION_DATE
          ),
        }),
      });
    }
  }, [activeRegistrations]);

  const watchRegistrationDate = watch('registration_date');
  React.useEffect(() => {
    // console.log(props);
    if (
      props.isExcluded &&
      watchRegistrationDate !== null &&
      props.scheme === SchemeTypes.IOSS
    ) {
      const exclusionEffectiveDate = new Date(watchRegistrationDate);
      if (props.exclusionReason === '4') {
        // exclusion reason == 4
        exclusionEffectiveDate.setDate(exclusionEffectiveDate.getDate() + 1);
      } else {
        // exclusion reason != 4
        exclusionEffectiveDate.setMonth(exclusionEffectiveDate.getMonth() + 1);
        exclusionEffectiveDate.setDate(1);
      }
      setValue('commencement_date', exclusionEffectiveDate);
    }
  }, [watchRegistrationDate]);

  const onSubmit = (data: any) => {
    const flagDate = new Date(2021, 6, 1);
    const registrationDate = new Date(data.registration_date);
    registrationDate.setHours(0, 0, 0, 0);
    const commencementDate = new Date(data.commencement_date);
    commencementDate.setHours(0, 0, 0, 0);
    if (
      props.scheme === SchemeTypes.IOSS &&
      registrationDate > flagDate &&
      registrationDate.getTime() != commencementDate.getTime() &&
      !props.isExcluded
    ) {
      setError('registration_date', {
        type: 'manual',
        message: `Registration date and commencement date should be the same when scheme is IMPORT`,
      });
      return;
    }

    if (data.response === 'approve') {
      dispatch(
        RegistrationDetailActions.approveRegistration({
          id: props.registration_id,
          comments: data.comments,
          registration_date: DateUtils.convertToUtc(data.registration_date),
          commencement_date: DateUtils.convertToUtc(data.commencement_date),
          // registration_date: data.registration_date,
          // commencement_date: data.commencement_date,
          isExcluded: props.isExcluded,
        })
      );
    } else if (data.response === 'reject') {
      dispatch(
        RegistrationDetailActions.rejectRegistration({
          id: props.registration_id,
          comments: data.comments,
          registration_date: DateUtils.convertToUtc(data.registration_date),
          commencement_date: DateUtils.convertToUtc(data.commencement_date),
          // registration_date: data.registration_date,
          // commencement_date: data.commencement_date,
          isExcluded: props.isExcluded,
        })
      );
    }
  };

  return (
    <div>
      <form
        className={classes.form}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <GridLayout
          elements={[
            {
              element: renderRadioGroupField(errors, control),
              size: 12,
              id: 'decision',
            },
            {
              element: renderRegistrationIdField(errors, control),
              size: 12,
              id: 'id',
            },
            {
              element: renderRegistrationDateField(
                errors,
                control,
                props.isExcluded
              ),
              size: 12,
              id: 'registration_date',
            },
            {
              element: renderCommencementDateField(
                errors,
                control,
                props.isExcluded,
                props.scheme
              ),
              size: 12,
              id: 'commencement_date',
            },
            {
              element: renderCommentsField(errors, control, props.isExcluded),
              size: 12,
              id: 'comments',
            },
          ]}
        />
        <Button
          style={{ width: '100%' }}
          variant="contained"
          color="primary"
          type="submit"
        >
          <TranslatedText textMap={Translations.submit} />
        </Button>
      </form>
    </div>
  );
}

const renderRadioGroupField = (
  errors: DeepMap<IApproveReject, FieldError>,
  control: Control<IApproveReject>
) => {
  return (
    <AppRadiogroup
      row
      id="response"
      label=""
      error={'response' in errors}
      name="response"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={errors.response && errors.response.message}
      values={[
        {
          value: 'approve',
          label: <TranslatedText textMap={Translations.approve} />,
        },
        {
          value: 'reject',
          label: <TranslatedText textMap={Translations.reject} />,
        },
      ]}
    />
  );
};

const renderRegistrationIdField = (
  errors: DeepMap<IApproveReject, FieldError>,
  control: Control<IApproveReject>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="registration_id"
      label="ID"
      error={'registration_id' in errors}
      name="registration_id"
      control={control}
      rules={{}}
      helperText={errors.registration_id && errors.registration_id.message}
      disabled={true}
    />
  );
};

const renderCommencementDateField = (
  errors: DeepMap<IApproveReject, FieldError>,
  control: Control<IApproveReject>,
  isExcluded: boolean,
  scheme: SchemeTypes
) => {
  return (
    <AppDatePicker
      margin="normal"
      required
      fullWidth
      id="commencement_date"
      label={isExcluded ? 'Exclusion Effective Date' : 'Commencement Date'}
      error={'commencement_date' in errors}
      name="commencement_date"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={errors.commencement_date && errors.commencement_date.message}
      autoOk
      disabled={isExcluded && scheme === SchemeTypes.IOSS}
    />
  );
};

const renderRegistrationDateField = (
  errors: DeepMap<IApproveReject, FieldError>,
  control: Control<IApproveReject>,
  isExcluded: boolean
) => {
  return (
    <AppDatePicker
      margin="normal"
      required
      fullWidth
      id="registration_date"
      error={'registration_date' in errors}
      name="registration_date"
      label={isExcluded ? 'Exclusion Decision Date' : 'Registration Date'}
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={errors.registration_date && errors.registration_date.message}
      autoOk
    />
  );
};

const renderCommentsField = (
  errors: DeepMap<IApproveReject, FieldError>,
  control: Control<IApproveReject>,
  isExcluded: boolean
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="comments"
      label={isExcluded ? 'Exclusion comments' : 'Comments'}
      error={'comments' in errors}
      name="comments"
      control={control}
      rules={{}}
      helperText={errors.comments && errors.comments.message}
      multiline
      rows={4}
      rowsMax={6}
    />
  );
};
